import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StarField } from "@/components/StarField";

const CONTENT = {
  about: {
    title: "About Us",
    body: [
      "MyssticChat.in is a premium Indian astrology subscription service offering personalized monthly predictions and traditional remedies. Our mission is to make thoughtful, elegant astrological guidance accessible for personal reflection.",
      "Each month, subscribers receive a tailored reading based on their unique birth details, covering career, finance, love, health, family, travel, opportunities, challenges, important dates, lucky days and personalized remedies.",
      "We believe astrology is a tool for reflection and intention — never a guarantee. We present our guidance with honesty, care and respect for your privacy.",
    ],
  },
  contact: {
    title: "Contact",
    body: [
      "We'd love to hear from you. For support, subscription queries or feedback, reach us at support@myssticchat.in.",
      "Our team typically responds within 1–2 business days. For account-specific requests, please write from your registered email address.",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      "We treat your personal information — including your name, email, mobile number and birth details — as private data. It is collected solely to deliver your personalized astrology predictions and service notifications.",
      "We never sell your data. Birth details, phone numbers and email addresses are stored securely and are never exposed to other users or through public URLs.",
      "You may request access to, correction of, or deletion of your data at any time. Communication preferences (email, WhatsApp, marketing) can be updated from your dashboard, and you can opt out at any time.",
      "We use secure authentication, password hashing, encryption for sensitive data where appropriate, and audit logging to protect your account.",
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      "By using MyssticChat, you agree to these terms. MyssticChat provides astrology-based content for personal reflection and entertainment.",
      "Predictions and remedies are not guaranteed outcomes and must not be treated as professional medical, legal, financial or investment advice.",
      "You are responsible for maintaining the confidentiality of your account credentials. Accounts must use verified, unique email and mobile combinations.",
      "We reserve the right to suspend accounts involved in fraud, abuse or breach of these terms.",
    ],
  },
  subscription: {
    title: "Subscription Terms",
    body: [
      "MyssticChat Monthly is priced at ₹499/month and renews automatically each month until cancelled.",
      "You may cancel anytime from your dashboard; cancellation stops future renewals. Access continues until the end of the current paid period as applicable.",
      "Payments are processed securely through our payment gateway. We do not store raw card information.",
      "Prices and plans may change with prior notice. Additional plans (6/12/24 months) may be introduced in the future.",
    ],
  },
  refund: {
    title: "Refund / Cancellation Policy",
    body: [
      "You can cancel your subscription at any time from your dashboard to prevent future charges.",
      "As predictions are personalized digital content delivered monthly, fees for a period during which content has already been delivered are generally non-refundable, except where required by applicable law.",
      "If you believe you were charged in error, contact support@myssticchat.in within 7 days and we will review your request.",
    ],
  },
  "lucky-draw": {
    title: "Lucky Draw Terms",
    body: [
      "The 24-Month Subscriber Lucky Draw is a promotional program. Subscribers who maintain an eligible, continuously paid subscription for 24 consecutive months may become eligible for a promotional draw.",
      "Eligibility requires 24 consecutive successful, non-refunded monthly payments with no lapse. Missed payments, cancellation, refunds, fraud or breach of terms result in disqualification.",
      "Winners are selected via an auditable, randomized process with a recorded seed and audit log, and are published only after administrator verification.",
      "The prize is a 1 BHK apartment, configurable by the administrator to another approved prize. Winning is never guaranteed.",
      "This program is offered only subject to applicable Indian laws, consumer rules, tax requirements and any state-specific promotional/contest requirements. Terms & Conditions apply and may be updated.",
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    body: [
      "MyssticChat provides astrology-based guidance for personal reflection and entertainment. Astrology predictions are not guaranteed outcomes and should not be considered professional medical, legal, financial or investment advice.",
      "Remedies such as mantras, charity and lifestyle suggestions are traditional practices offered for personal reflection and must not replace professional advice. Always consult a qualified professional for medical, legal or financial matters.",
    ],
  },
  cookies: {
    title: "Cookie Policy",
    body: [
      "We use essential cookies to keep you signed in securely and to operate core features of the site. Authentication uses secure, httpOnly cookies.",
      "We may use limited analytics to understand and improve the experience. You can control cookies through your browser settings, though disabling essential cookies may affect functionality.",
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const page = CONTENT[slug] || { title: "Not Found", body: ["This page does not exist."] };

  return (
    <div className="bg-[#080B1A] min-h-screen text-slate-100">
      <Navbar />
      <section className="relative pt-32 pb-16 overflow-hidden">
        <StarField count={50} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6">
          <span className="text-xs uppercase tracking-[0.2em] text-amber-400/90">Legal</span>
          <h1 data-testid="legal-title" className="mt-3 font-serif text-4xl sm:text-5xl text-slate-50">{page.title}</h1>
          <div className="mt-8 space-y-5">
            {page.body.map((p, i) => (
              <p key={i} className="text-slate-300 leading-relaxed">{p}</p>
            ))}
          </div>
          <p className="mt-10 text-sm text-slate-500">Last updated {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long" })}. Terms &amp; Conditions Apply.</p>
          <Link to="/" className="mt-8 inline-block text-amber-300 hover:text-amber-200">← Back to home</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
